import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { IFCM, IRequestPwdRecovery, IResetPassword, IUserCredentials, } from '../data-types/user.interface';
import { HttpClientService, versionEnum } from './http-client.service';
import { EStorageType } from '../data-types/storage.enum';

@Injectable({
  providedIn: "root",
})
export class UserService {
  static instance: UserService = new UserService();
  constructor() {
    return (UserService.instance = UserService.instance || this);
  }
  public async login(email, password, tokenFirebase, isMobile): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "auth/token";
      let headers: any = {};
      let body: IUserCredentials = {
        email,
        pwd: password,
      };

      if (tokenFirebase) {
        body.firebase_token = tokenFirebase;
      }

      HttpClientService.instance
        .post(url, headers, body, versionEnum.V2)
        .then((result: any) => {
          const role = this.defineRole(result.roles, isMobile);
          if (isMobile && role != 'coworker') {
            resolve({error: 'InvalidRole'});
          } 
          LocalStorageService.instance.set(EStorageType.Token, result.token);
          LocalStorageService.instance.set(EStorageType.PostalCode, result.postal_code);
          LocalStorageService.instance.set(
            EStorageType.RefresToken,
            result.refresh_token
          );
          LocalStorageService.instance.set(EStorageType.Role, this.defineRole(result.roles, isMobile));
          if (result.coworkings) {
            const coworking = result.coworkings.coworkings.map(
              (coworking, i) => {
                if (i === 0) {
                  coworking.active = true;
                  return coworking;
                } else {
                  coworking.active = false;
                  return coworking;
                }
              }
            );

            LocalStorageService.instance.set(
              EStorageType.Coworking,
              JSON.stringify(coworking)
            );
            LocalStorageService.instance.set(
              EStorageType.ActiveCoworking,
              result.coworkings.coworkings[0].id
            );
          }
          if (result.companies) {
            const company = result.companies.companies.map((company, i) => {
              if (i === 0) {
                company.active = true;
                return company;
              } else {
                company.active = false;
                return company;
              }
            });

            LocalStorageService.instance.set(
              EStorageType.Company,
              JSON.stringify(company)
            );
            LocalStorageService.instance.set(
              EStorageType.ActiveCompany,
              result.companies.companies[0].id
            );
          }

          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async refreshToken(): Promise<any> {
    const token: string = LocalStorageService.instance.get(EStorageType.Token);
    const refreshToken: string = LocalStorageService.instance.get(
      EStorageType.RefresToken
    );
    return new Promise((resolve, reject) => {
      const url = 'auth/token/refresh';
      const headers: any = {
      };
      const body = { token, refresh_token: refreshToken };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          LocalStorageService.instance.set(EStorageType.Token, result.token);
          resolve(result);
        })
        .catch((err) => {
          if (err.status == 400) {
            LocalStorageService.instance.clear();
            HttpClientService.instance.unauthorizedSubject.next();
          }
          reject(err);
        });
    });
  }

  public async resetPwd(password, email, token): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "auth/pwd/reset";
      let headers: any = {};
      let body: IResetPassword = {
        email,
        token,
        password,
      };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async sendTokenJCM(token): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = 'jcm/token';
      const headers = {};
      const body: IFCM = {
        token
      };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async requestPwdRecovery(email): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "auth/pwd/recovery";
      let headers: any = {};
      let body: IRequestPwdRecovery = {
        email: email,
      };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async usersCheckEmailList(emails): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = `companies/coworkers/check`;
      let headers: any = {};
      let body: any = {
        emails: emails,
      };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  private defineRole(roles: string[], isMobile: boolean): string {
    const companyPlus = ['company', 'coworking'];
    const companyPlusCoworker = ['company', 'coworking', 'coworker'];
    const company = ['company'];
    const companyCoworker = ['company', 'coworker'];
    const coworking = ['coworking'];
    const coworker = ['coworker'];
    const admin = ['admin'];
    const isCompanyPlus = this.existRole(roles, companyPlus);
    const isCompanyPlusCoworker = this.existRole(roles, companyPlusCoworker);
    const isCompany = this.existRole(roles, company);
    const isCompanyCoworker = this.existRole(roles, companyCoworker);
    const isCoworking = this.existRole(roles, coworking);
    const isCoworker = this.existRole(roles, coworker);
    const isAdmin = this.existRole(roles, admin);
    let role = '';
    if (isCompanyPlus) {
      role = 'company-plus';
    } else if (isCompany) {
      role = 'company';
    } else if (isCoworking) {
      role = 'coworking';
    } else if (isCoworker) {
      role = 'coworker';
    } else if (isCompanyPlusCoworker) {
      role = isMobile ? 'coworker' : 'company-plus';
    } else if (isCompanyCoworker) {
      role = isMobile ? 'coworker' : 'company';
    } else if (isAdmin) {
      role = 'admin';
    }
    return role;
  }

  private existRole(roles: string[], role: string[]): boolean {
    if (roles.length !== role.length) {
      return false;
    }
    for (let i = 0; i < roles.length; i++) {
      if (roles[i] !== role[i]) {
        return false;
      }
    }
    return true;
  }
}
